import { useState, useEffect, useRef, useContext } from 'react';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlSpinner from '@shoelace-style/shoelace/dist/react/spinner/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { Author, Pagination, useDeferredLoading } from '@anticatrade/shared';
import { appContext, AppContext } from '../../../context/AppContext';
import { useTranslation } from 'react-i18next';

const AUTHORS_PER_PAGE = 20;

interface AuthorPickerDialogProps {
	isOpen: boolean;
	selectedID?: number;
	onHide: () => void;
	onSelect: (author: Author) => void;
	onProposeAuthor: () => void;
}

const AuthorPickerDialog = ({
	isOpen,
	selectedID,
	onHide,
	onSelect,
	onProposeAuthor,
}: AuthorPickerDialogProps) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [page, setPage] = useState(1);
	const [authors, setAuthors] = useState<Author[]>([]);
	const [count, setCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const abortRef = useRef<AbortController>();

	const searchInputRef = useRef<any>(null);

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const { t } = useTranslation();
	const { api, handleError } = useContext(appContext as React.Context<AppContext>);

	const fetchAuthors = async (page: number, term: string) => {
		if (abortRef.current) {
			abortRef.current.abort();
		}
		const controller = new AbortController();
		abortRef.current = controller;

		setIsLoading(true);
		try {
			const res = await api.searchAuthors(page, AUTHORS_PER_PAGE, term, {
				signal: controller.signal,
			});
			setAuthors(res.authors);
			setCount(res.count);
		} catch (err: any) {
			if (err?.name !== 'AbortError') {
				handleError(err);
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (isOpen) {
			fetchAuthors(1, '');
		}
	}, [isOpen]);

	const onSearchInput = (e: any) => {
		const term = e.target.value;
		setSearchTerm(term);
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		debounceRef.current = setTimeout(() => {
			setPage(1);
			fetchAuthors(1, term);
		}, 300);
	};

	const onPageChange: React.Dispatch<React.SetStateAction<number>> = (newPage) => {
		const p = typeof newPage === 'function' ? newPage(page) : newPage;
		setPage(p);
		fetchAuthors(p, searchTerm);
	};

	const onAfterHide = () => {
		onHide();
		setIsLoading(false);
		setSearchTerm('');
		setPage(1);
		setAuthors([]);
		setCount(0);
	};

	return (
		<SlDialog
			className='product__author-picker-dialog'
			label={t('Author')}
			open={isOpen}
			onSlAfterHide={onAfterHide}
			onSlAfterShow={() => { if (window.innerWidth > 900) searchInputRef.current?.focus(); }}
		>
			<SlInput
				ref={searchInputRef}
				className='product__author-picker-search'
				placeholder={t('Search by name...')}
				onSlInput={onSearchInput}
				value={searchTerm}
				size='small'
				clearable
			>
				<SlIcon name='x-circle' slot='clear-icon' />
			</SlInput>
			{showSpinner ? (
				<div className='product__author-picker-loading'>
					<SlSpinner
						style={{ fontSize: '43px', '--track-width': '6px' } as React.CSSProperties}
					/>
				</div>
			) : showContent ? (
				authors.length === 0 ? (
					<div className='product__author-picker-empty'>{t('No authors found')}</div>
				) : (
					<div className='product__author-picker-list'>
						{authors.map((author) => (
							<button
								key={author.id}
								className='product__author-picker-item'
								onClick={() => onSelect(author)}
							>
								{author.id === selectedID && (
									<SlIcon className='product__picker-check' name='check-lg' />
								)}
								{author.name}
							</button>
						))}
					</div>
				)
			) : (
				<div className='product__author-picker-loading' />
			)}
			{count > AUTHORS_PER_PAGE && (
				<Pagination
					count={count}
					limit={AUTHORS_PER_PAGE}
					page={page}
					setPage={onPageChange}
					t={t}
				/>
			)}
			<div className='product__author-picker-footer'>
				{t("Can't find the author you're looking for?")}{' '}
				<button onClick={onProposeAuthor}>{t('Create it!')}</button>
			</div>
		</SlDialog>
	);
};

export { AuthorPickerDialog };
